import OnBoardingLayout from "../OnBoardingLayout.jsx";
import FormContainer from "../../../../components/form/FormContainer.jsx";
import LoadingComponent from "../../../../components/LoadingComponent.jsx";
import useDownloadState from "../../../../api/utilities/useDownloadState.js";
import {useContext, useEffect, useRef, useState} from "react";
import FormControl from "../../../../components/form/FormControl.jsx";
import {useTranslation} from "react-i18next";
import Button from "../../../../components/Button.jsx";
import {GlobalContext} from "../../../../utils/context/GlobalContextProvider.jsx";
import {IntegrationsApi} from "../../../../api/authority/generated/index.ts";
import FormError from "../../../../components/form/FormError.jsx";
import {useNavigate} from "react-router-dom";
import {
    getLocalStoragePendingAddon,
    setLocalStoragePendingAddon,
    setLocalStorageUpgatesPendingToken
} from "../../../../utils/localStorage.js";
import {
    acquireAddonSubmissionLock,
    cancelPendingAddonConnection,
    getAddonProvisionPayload,
    getAddonProvisionRecovery,
    getAddonProvisionDestination,
    retainAddonProvisionTarget,
    retainAddonProvisionResponse,
} from "../../../../utils/onboardingRouting.js";

export default function AddonProjectStep() {
    let navigate = useNavigate();
    let {t} = useTranslation();
    let {
        context,
        getAuthorityConfiguration,
        setCurrentProjectId,
        resetCurrentUserProjects,
    } = useContext(GlobalContext);

    const initialPendingAddon = getLocalStoragePendingAddon();
    const initialRecovery = getAddonProvisionRecovery(initialPendingAddon);
    let [selectedProjectId, setSelectedProjectId] = useState(initialRecovery.projectId ?? '');
    let [newProjectName, setNewProjectName] = useState(initialRecovery.projectName ?? '');
    let [createNewProject, setCreateNewProject] = useState(initialRecovery.projectName !== null);
    let [pendingNavigateProjectId, setPendingNavigateProjectId] = useState(initialRecovery.responseProjectId);

    let [state, setDownloading, , setError] = useDownloadState();
    let [submitPressed, setSubmitPressed] = useState(false);
    const submissionLock = useRef(false);
    const submissionInProgress = state.downloading || submitPressed;
    const selectableProjects = context.currentUserProjects.filter(
        (project) => !project.hasErpIntegration && !project.hasLeadIntegration
    );
    const hasAvailableProjects = selectableProjects.length > 0;
    const clearPendingTokenAndRedirectHome = (flashMessage = null) => {
        if (flashMessage) {
            sessionStorage.setItem('_flashErrorMessage', flashMessage);
        }
        localStorage.removeItem('_upgatesPendingToken');
        localStorage.removeItem('_pendingAddon');
        setLocalStoragePendingAddon(null);
        setLocalStorageUpgatesPendingToken(null);
        window.location.assign('/');
    };

    useEffect(() => {
        if (!submitPressed) {
            return;
        }

        setSubmitPressed(false);
        setDownloading(true);

        const pendingAddon = getLocalStoragePendingAddon();
        if (!pendingAddon) {
            clearPendingTokenAndRedirectHome();
            return;
        }

        const recovery = getAddonProvisionRecovery(pendingAddon);
        if (recovery.responseProjectId) {
            setPendingNavigateProjectId(recovery.responseProjectId);
            resetCurrentUserProjects();
            return;
        }

        if (recovery.projectId || recovery.projectName) {
            provisionAddon(pendingAddon);
            return;
        }

        if (createNewProject && newProjectName.trim() !== '') {
            const retainedPendingAddon = retainAddonProvisionTarget(pendingAddon, {
                projectName: newProjectName.trim(),
            });
            setLocalStoragePendingAddon(retainedPendingAddon);
            provisionAddon(retainedPendingAddon);

            return;
        }

        if (hasAvailableProjects && selectedProjectId) {
            const retainedPendingAddon = retainAddonProvisionTarget(pendingAddon, {
                projectId: selectedProjectId,
            });
            setLocalStoragePendingAddon(retainedPendingAddon);
            provisionAddon(retainedPendingAddon);
            return;
        }

        setError(new Error(t('onboarding.select-or-create-project')));
        setDownloading(false);
        submissionLock.current = false;
    }, [
        context.currentIdentity,
        createNewProject,
        getAuthorityConfiguration,
        newProjectName,
        hasAvailableProjects,
        selectedProjectId,
        submitPressed,
        t,
    ]);

    useEffect(() => {
        if (pendingNavigateProjectId === null || !context.currentUserProjectsLoaded) {
            return;
        }

        const destination = getAddonProvisionDestination({
            responseProjectId: pendingNavigateProjectId,
            projects: context.currentUserProjects,
            isAdministrator: context.currentIdentity?.user?.isAdministrator === true,
        });
        if (!destination) {
            setPendingNavigateProjectId(null);
            setError(t('onboarding.addon-provision-project-unavailable', {
                defaultValue: 'Doplněk byl připojen, ale propojený projekt se nepodařilo načíst. Zkuste pokračovat znovu. Pokud problém přetrvá, kontaktujte podporu.',
            }));
            setDownloading(false);
            submissionLock.current = false;
            return;
        }

        setLocalStoragePendingAddon(null);
        setLocalStorageUpgatesPendingToken(null);
        setCurrentProjectId(destination.projectId);
        setPendingNavigateProjectId(null);
        navigate(destination.path);
    }, [context.currentIdentity, context.currentUserProjects, context.currentUserProjectsLoaded, navigate, pendingNavigateProjectId, setCurrentProjectId]);

    const provisionAddon = (pendingAddon) => {
        new IntegrationsApi(getAuthorityConfiguration())
            .addonProvisionProject({
                addonProvisionProjectRequest: getAddonProvisionPayload(pendingAddon),
            })
            .then((response) => {
                if (response?.error) {
                    setError(t('onboarding.addon-provision-failed', {
                        defaultValue: 'Doplněk se nepodařilo připojit. Zkuste to prosím znovu. Již vytvořený projekt použijeme znovu.',
                    }));
                    setDownloading(false);
                    submissionLock.current = false;
                    return;
                }

                if (typeof response?.projectId !== 'string' || response.projectId.trim() === '') {
                    setError(t('onboarding.addon-provision-failed', {
                        defaultValue: 'Doplněk se nepodařilo bezpečně připojit. Zkuste to prosím znovu. Již vytvořený projekt použijeme znovu.',
                    }));
                    setDownloading(false);
                    submissionLock.current = false;
                    return;
                }

                setLocalStoragePendingAddon(retainAddonProvisionResponse(pendingAddon, response.projectId));
                setPendingNavigateProjectId(response.projectId);
                resetCurrentUserProjects();
            })
            .catch(() => {
                setError(t('onboarding.addon-provision-failed', {
                    defaultValue: 'Doplněk se nepodařilo připojit. Zkontrolujte připojení a zkuste to znovu. Již vytvořený projekt použijeme znovu.',
                }));
                setDownloading(false);
                submissionLock.current = false;
            });
    };

    return (
        <OnBoardingLayout title={!hasAvailableProjects ? t('project.create') : undefined}>
            <FormContainer>
                <LoadingComponent loading={submissionInProgress}>
                    {state.error && <FormError error={state.error}/>}

                    {hasAvailableProjects && (
                        <div className="mb-6">
                            <label className="block text-sm font-medium mb-2">
                                {t('onboarding.select-project-for-upgates')}
                            </label>
                            <select
                                value={selectedProjectId}
                                onChange={(event) => {
                                    setSelectedProjectId(event.target.value);
                                    setCreateNewProject(false);
                                }}
                                className="w-full rounded-lg border-2 border-transparent bg-white px-4 py-4 text-sm font-medium leading-[22px] text-slate-800 shadow-sm transition duration-150 ease-in-out hover:border-slate-200 focus:border-[#8470ff] focus:outline-none focus:ring-0"
                            >
                                <option value="">{t('onboarding.choose-project')}</option>
                                {selectableProjects.map((project) => (
                                    <option key={project.id} value={project.id}>
                                        {project.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    <div className={hasAvailableProjects ? 'border-t pt-6' : ''}>
                        <FormControl
                            type={'text'}
                            label={hasAvailableProjects
                                ? t('onboarding.or-create-new-project')
                                : t('project.name')}
                            value={newProjectName}
                            onChange={(event) => {
                                setNewProjectName(event.target.value);

                                if (event.target.value.trim() !== '') {
                                    setCreateNewProject(true);
                                    setSelectedProjectId('');
                                }
                            }}
                            placeholder={t('onboarding.enter-new-project-name')}
                            variant={'onboarding'}
                        />
                    </div>

                    <div className="mt-6 flex items-center justify-between gap-3">
                        <Button
                            variant="outline-primary"
                            onClick={() => cancelPendingAddonConnection({
                                identityId: context.currentIdentity.user.id,
                            })}
                            disabled={submissionInProgress}
                        >
                            {t('onboarding.cancel-addon-connection')}
                        </Button>
                        <Button
                            className="button-primary"
                            onClick={() => {
                                if (acquireAddonSubmissionLock(submissionLock)) {
                                    setSubmitPressed(true);
                                }
                            }}
                            disabled={submissionInProgress || (!selectedProjectId && (!createNewProject || newProjectName.trim() === ''))}
                        >
                            {t('onboarding.connect-addon')}
                        </Button>
                    </div>
                </LoadingComponent>
            </FormContainer>
        </OnBoardingLayout>
    );
}
